/**
 * Sysadmin Catalog → Workflows (PR-K1).
 *
 * List view for system workflow templates with:
 *   - filter chips (status, category)
 *   - search by name/slug/description
 *   - cursor-paginated table (Load more button)
 *   - "New Workflow" button → create drawer
 *
 * Detail view at /sysadmin/catalog/workflows/$id (catalog.workflows.$id.tsx).
 *
 * Lifecycle: every workflow row carries a status pill (draft / published /
 * deprecated / retired). Tenants only see published workflows in their
 * "available to clone" list (CLONE inheritance — tenants fork a snapshot
 * into their own workspace).
 *
 * Mirrors catalog.skills.tsx + catalog.tools.tsx patterns. No mock data.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Workflow,
  Search,
  AlertTriangle,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  useCatalogWorkflows,
  useCreateCatalogWorkflow,
  type WorkflowStatus,
} from '@/lib/use-sysadmin-catalog-workflows';

// R5 P0-1 fix (2026-05-13): list now lives under index route ID
// `/sysadmin/catalog/workflows/`; parent `catalog.workflows.tsx` is a
// pure layout with <Outlet/> so detail children can render.
export const Route = createFileRoute('/sysadmin/catalog/workflows/')({
  component: SysadminCatalogWorkflowsPage,
});

const STATUS_FILTERS: Array<{ key: WorkflowStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'deprecated', label: 'Deprecated' },
  { key: 'retired', label: 'Retired' },
];

function statusPillClass(status: WorkflowStatus): string {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'draft':
      return 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30';
    case 'deprecated':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'retired':
      return 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-400 border-zinc-500/30';
  }
}

function SysadminCatalogWorkflowsPage() {
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | 'all'>(
    'all',
  );
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const list = useCatalogWorkflows({
    status: statusFilter === 'all' ? undefined : statusFilter,
    category: categoryFilter || undefined,
    search: search.trim() || undefined,
    limit: 50,
  });

  const allItems = useMemo(
    () => list.data?.pages.flatMap((p) => p.items) ?? [],
    [list.data],
  );

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((w) => w.category && set.add(w.category));
    return Array.from(set).sort();
  }, [allItems]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Workflow className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Workflow Templates
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">
              §3.3
            </Badge>
            <Badge variant="secondary" className="text-xs">
              v1
            </Badge>
            <Badge variant="outline" className="text-xs">
              CLONE
            </Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Catalog</CardTitle>
          <CardDescription>
            Sysadmin-authored deterministic playbooks. A workflow composes
            references to system skills, tools, and functions plus a step
            definition. Inheritance is{' '}
            <span className="font-mono">CLONE</span> — tenants fork a
            snapshot of a published workflow into their workspace, then
            modify independently.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Filters + search + new button */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Status:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs border transition-colors',
                statusFilter === f.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:border-foreground/40',
              )}
              title={`Filter by ${f.label.toLowerCase()}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {categoryOptions.length > 0 && (
            <>
              <span className="text-xs text-muted-foreground mr-1">
                Category:
              </span>
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  !categoryFilter
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:border-foreground/40',
                )}
              >
                All
              </button>
              {categoryOptions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoryFilter(c)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs border transition-colors',
                    categoryFilter === c
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:border-foreground/40',
                  )}
                >
                  {c}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by name, slug, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <Button
            size="sm"
            className="gap-1.5 ml-auto"
            onClick={() => setShowCreate(true)}
            title="Create a new draft workflow template"
          >
            <Plus className="h-3.5 w-3.5" /> New Workflow
          </Button>
        </div>
      </div>

      {list.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading workflows…
          </CardContent>
        </Card>
      ) : list.error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {list.error.message}</div>
          </CardContent>
        </Card>
      ) : allItems.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No workflows match the current filters.
            {(statusFilter !== 'all' || categoryFilter || search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setCategoryFilter('');
                  setSearch('');
                }}
                className="ml-2"
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32">Slug</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  <TableHead className="w-20 text-right">Skills</TableHead>
                  <TableHead className="w-20 text-right">Tools</TableHead>
                  <TableHead className="w-20 text-right">Fns</TableHead>
                  <TableHead className="w-20 text-right">Version</TableHead>
                  <TableHead className="w-24 text-right">Clones</TableHead>
                  <TableHead className="w-44">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.map((w) => (
                  <TableRow
                    key={w.id}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell>
                      <Link
                        to="/sysadmin/catalog/workflows/$id"
                        params={{ id: w.id }}
                        className="block"
                      >
                        <div className="text-sm font-medium">
                          {w.displayName}
                        </div>
                        {w.description && (
                          <div className="text-[11px] text-muted-foreground line-clamp-1 max-w-md">
                            {w.description}
                          </div>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/sysadmin/catalog/workflows/$id"
                        params={{ id: w.id }}
                        className="font-mono text-xs"
                      >
                        {w.slug}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] capitalize border',
                          statusPillClass(w.status),
                        )}
                      >
                        {w.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {w.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {w.referencedSkills.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {w.referencedTools.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {w.referencedFunctions.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      v{w.version}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {w.cloneCount}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(w.updatedAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {list.hasNextPage && (
              <div className="border-t px-4 py-3 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      Loading more…
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CreateWorkflowSheet open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}

// ─── Create drawer ────────────────────────────────────────────────────────

interface CreateDraft {
  slug: string;
  displayName: string;
  description: string;
  category: string;
  triggerMode: 'conversational' | 'scheduled' | 'event';
}

const EMPTY_CREATE: CreateDraft = {
  slug: '',
  displayName: '',
  description: '',
  category: 'general',
  triggerMode: 'conversational',
};

function CreateWorkflowSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [draft, setDraft] = useState<CreateDraft>(EMPTY_CREATE);
  const create = useCreateCatalogWorkflow();

  const handleCreate = async () => {
    if (!draft.slug || !draft.displayName || !draft.description) {
      return;
    }
    try {
      await create.mutateAsync({
        slug: draft.slug,
        displayName: draft.displayName,
        description: draft.description,
        category: draft.category || 'general',
        triggerConfig: { mode: draft.triggerMode },
        definition: {},
        inputSchema: {},
        outputSchema: {},
        referencedSkills: [],
        referencedTools: [],
        referencedFunctions: [],
      });
      toast.success(`Created draft "${draft.displayName}"`);
      setDraft(EMPTY_CREATE);
      onOpenChange(false);
    } catch (err) {
      toast.error('Create failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Workflow</SheetTitle>
          <SheetDescription>
            Creates a draft. Add the step definition, references, and I/O
            schemas on the detail page, then publish from the lifecycle
            action bar. Tenants will be able to clone it after publish.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cw-slug" className="text-xs">
                Slug
              </Label>
              <Input
                id="cw-slug"
                value={draft.slug}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    slug: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_.-]/g, ''),
                  })
                }
                placeholder="equipment-troubleshooting"
                className="font-mono text-xs h-8"
              />
              <p className="text-[10px] text-muted-foreground">
                lowercase. a-z, 0-9, _ - .
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cw-category" className="text-xs">
                Category
              </Label>
              <Input
                id="cw-category"
                value={draft.category}
                onChange={(e) =>
                  setDraft({ ...draft, category: e.target.value })
                }
                placeholder="general"
                className="text-xs h-8"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cw-name" className="text-xs">
              Display name
            </Label>
            <Input
              id="cw-name"
              value={draft.displayName}
              onChange={(e) =>
                setDraft({ ...draft, displayName: e.target.value })
              }
              placeholder="Equipment Troubleshooting"
              className="text-sm h-8"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cw-desc" className="text-xs">
              Description
            </Label>
            <Textarea
              id="cw-desc"
              value={draft.description}
              onChange={(e) =>
                setDraft({ ...draft, description: e.target.value })
              }
              placeholder="What this workflow does and when to use it."
              rows={3}
              className="text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cw-trigger" className="text-xs">
              Trigger mode
            </Label>
            <Select
              value={draft.triggerMode}
              onValueChange={(v) =>
                setDraft({
                  ...draft,
                  triggerMode: v as CreateDraft['triggerMode'],
                })
              }
            >
              <SelectTrigger
                id="cw-trigger"
                className="text-sm h-8"
                title="How the workflow is invoked"
              >
                <SelectValue placeholder="Trigger mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="conversational">
                  Conversational — invoked by user message
                </SelectItem>
                <SelectItem value="scheduled">
                  Scheduled — cron-based
                </SelectItem>
                <SelectItem value="event">
                  Event — fired by alarm / threshold / webhook
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_CREATE);
                onOpenChange(false);
              }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={
                !draft.slug ||
                !draft.displayName ||
                !draft.description ||
                create.isPending
              }
            >
              {create.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create draft'
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
